import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CmmdProjectMilestone,
  CreateCmmdProjectMilestoneParams,
  UpdateCmmdProjectMilestoneParams,
} from "@cmmd-forge/contracts";

import { createCmmdApiClient } from "~/environments/cmmdApi";

const api = createCmmdApiClient();

function queryKey(projectId: number | null): readonly [string, number | null] {
  return ["cmmd-project-milestones", projectId];
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error && cause.message
    ? cause.message
    : "Could not update project milestones.";
}

export function projectMilestoneLoadError(
  data: readonly CmmdProjectMilestone[] | undefined,
  cause: unknown,
): string | null {
  return cause && data === undefined ? errorMessage(cause) : null;
}

function firstError(...causes: readonly unknown[]): string | null {
  for (const cause of causes) {
    if (cause) return errorMessage(cause);
  }
  return null;
}

export interface ProjectMilestonesController {
  readonly milestones: readonly CmmdProjectMilestone[];
  readonly loading: boolean;
  readonly mutating: boolean;
  readonly error: string | null;
  /** Catalogue query failure; mutation errors remain scoped to milestone management UI. */
  readonly loadError?: string | null;
  readonly create: (
    input: CreateCmmdProjectMilestoneParams,
  ) => Promise<CmmdProjectMilestone | null>;
  readonly update: (
    milestoneId: number,
    input: UpdateCmmdProjectMilestoneParams,
  ) => Promise<boolean>;
  readonly delete: (milestoneId: number) => Promise<boolean>;
  readonly refresh: () => Promise<void>;
}

/** Canonical milestone data and mutations shared by the project task toolbar and surface. */
export function useProjectMilestones(
  projectId: number | null,
  enabled: boolean,
): ProjectMilestonesController {
  const client = useQueryClient();
  const key = queryKey(projectId);
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.listProjectMilestones(String(projectId)),
    enabled: enabled && projectId !== null,
    staleTime: 30_000,
  });
  const createMutation = useMutation({
    mutationFn: (input: CreateCmmdProjectMilestoneParams) =>
      api.createProjectMilestone(String(projectId), input),
    onSuccess: () => client.invalidateQueries({ queryKey: key }),
  });
  const updateMutation = useMutation({
    mutationFn: (args: {
      readonly milestoneId: number;
      readonly input: UpdateCmmdProjectMilestoneParams;
    }) => api.updateProjectMilestone(String(projectId), args.milestoneId, args.input),
    onSuccess: () => client.invalidateQueries({ queryKey: key }),
  });
  const deleteMutation = useMutation({
    mutationFn: (milestoneId: number) => api.deleteProjectMilestone(String(projectId), milestoneId),
    onSuccess: () => client.invalidateQueries({ queryKey: key }),
  });

  return {
    milestones: query.data ?? [],
    loading: query.isLoading,
    mutating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    error: firstError(
      query.error,
      createMutation.error,
      updateMutation.error,
      deleteMutation.error,
    ),
    loadError: projectMilestoneLoadError(query.data, query.error),
    create: async (input) => {
      try {
        return await createMutation.mutateAsync(input);
      } catch {
        return null;
      }
    },
    update: async (milestoneId, input) => {
      try {
        await updateMutation.mutateAsync({ milestoneId, input });
        return true;
      } catch {
        return false;
      }
    },
    delete: async (milestoneId) => {
      try {
        await deleteMutation.mutateAsync(milestoneId);
        return true;
      } catch {
        return false;
      }
    },
    refresh: async () => {
      await query.refetch();
    },
  };
}
